import React, { useEffect, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import {
  agenticErrorMessage,
  useApproveAgenticBlueprintMutation,
  useGenerateAgenticBlueprintMutation,
  useGetAgenticAssessmentQuery,
  useResetAgenticAssessmentMutation,
  useSaveAgenticDraftMutation,
} from '../../../../../utils/api_service/agenticAssessmentApi';
import { AgenticConfig, AgenticDraft } from '../../../../../utils/types/agentic_assessment_types';
import { BlueprintEditor } from './BlueprintEditor';

interface SetupPanelProps {
  jobOpeningId: string;
  onGoToCandidates: () => void;
}

const DEFAULT_CONFIG: AgenticConfig = { maxQuestions: 15, maxProbesPerCompetency: 3 };
const textareaClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40';
const primaryButton =
  'rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors';
const secondaryButton =
  'rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors';

/** Anchors are all-or-nothing: empty means "write them for me", partial is a mistake. */
const draftProblem = (draft: AgenticDraft): string | null => {
  for (const c of draft.competencies) {
    if (!c.name.trim()) return 'Every competency needs a name.';
    if (c.anchors) {
      const filled = Object.values(c.anchors).filter((t) => t.trim()).length;
      if (filled > 0 && filled < 5) return `${c.name}: fill in all five levels, or clear them to have them written for you.`;
    }
  }
  return null;
};

const normaliseDraft = (draft: AgenticDraft): AgenticDraft => ({
  ...draft,
  competencies: draft.competencies.map((c) => {
    const anchorsEmpty = !c.anchors || Object.values(c.anchors).every((t) => !t.trim());
    return {
      ...c,
      description: c.description && c.description.trim() ? c.description : null,
      anchors: anchorsEmpty ? null : c.anchors,
    };
  }),
});

export const SetupPanel: React.FC<SetupPanelProps> = ({ jobOpeningId, onGoToCandidates }) => {
  const [polling, setPolling] = useState(false);
  const { data: assessment, isLoading } = useGetAgenticAssessmentQuery(jobOpeningId, {
    pollingInterval: polling ? 3000 : 0,
  });
  const [generate, { isLoading: isGenerating }] = useGenerateAgenticBlueprintMutation();
  const [save, { isLoading: isSaving }] = useSaveAgenticDraftMutation();
  const [approve, { isLoading: isApproving }] = useApproveAgenticBlueprintMutation();
  const [reset, { isLoading: isResetting }] = useResetAgenticAssessmentMutation();

  const [jobDescription, setJobDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [draft, setDraft] = useState<AgenticDraft | null>(null);
  const [config, setConfig] = useState<AgenticConfig>(DEFAULT_CONFIG);
  const [dirty, setDirty] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);

  const status = assessment?.status ?? null;
  const locked = !!assessment?.locked;

  useEffect(() => {
    setPolling(status === 'Generating');
  }, [status]);

  // Take the server copy whenever we have no unsaved edits.
  useEffect(() => {
    if (!assessment || dirty) return;
    setJobDescription(assessment.jobDescription || '');
    setNotes(assessment.recruiterNotes || '');
    setDraft(assessment.draft);
    setConfig(assessment.config || DEFAULT_CONFIG);
  }, [assessment, dirty]);

  if (isLoading || !assessment) {
    return <div className='py-16 text-center text-sm text-slate-500'>Loading the assessment…</div>;
  }

  const handleGenerate = async () => {
    try {
      await generate({ jobOpeningId, jobDescription, recruiterNotes: notes, config }).unwrap();
      setDirty(false);
      setShowRegenerate(false);
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not start generating the blueprint.'));
    }
  };

  /** Returns true when the saved draft is ready to approve (no rubric still being written). */
  const handleSave = async (): Promise<boolean> => {
    if (!draft) return false;
    const problem = draftProblem(draft);
    if (problem) {
      toast.error(problem);
      return false;
    }
    try {
      const saved = await save({ jobOpeningId, draft: normaliseDraft(draft), config }).unwrap();
      setDirty(false);
      if (saved.status === 'Generating') {
        toast.success('Saved. Writing rubrics for the new competencies; approve once they appear.');
        return false;
      }
      toast.success('Blueprint saved.');
      return true;
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not save the blueprint.'));
      return false;
    }
  };

  const handleApprove = async () => {
    if (dirty && !(await handleSave())) return;
    try {
      const approved = await approve(jobOpeningId).unwrap();
      toast.success(`Approved as version ${approved.approvedVersion}. You can now invite candidates.`);
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not approve the blueprint.'));
    }
  };

  const handleReset = async () => {
    try {
      await reset(jobOpeningId).unwrap();
      toast.success('Unlocked. Candidates already invited keep the version they were sent.');
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not unlock the blueprint.'));
    }
  };

  const generating = status === 'Generating';
  const showJdForm = !assessment.exists || status === 'Error' || (!draft && !generating) || showRegenerate;

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h3 className='text-base font-semibold text-slate-900'>Competency blueprint</h3>
          <p className='max-w-2xl text-sm text-slate-500'>
            Generated from the job description. Every candidate is scored against these competencies and target levels,
            so review them before you approve.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          { assessment.approvedVersion && (
            <span className='rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600'>
              Approved v{ assessment.approvedVersion }
            </span>
          ) }
          { status && (
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
              status === 'Approved' ? 'bg-emerald-50 text-emerald-700'
                : status === 'Error' ? 'bg-rose-50 text-rose-700'
                  : status === 'Generating' ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'
            }`}>
              { status }
            </span>
          ) }
        </div>
      </div>

      { locked && (
        <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3'>
          <p className='text-sm text-amber-800'>
            Invites have been sent, so the blueprint is locked to keep candidates comparable.
            Unlocking lets you edit and approve a new version; candidates already invited keep the version they received.
          </p>
          <button type='button' className={secondaryButton} disabled={isResetting} onClick={handleReset}>
            Unlock to edit
          </button>
        </div>
      ) }

      { status === 'Error' && (
        <div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          { assessment.errorMessage || 'Generating the blueprint failed.' } Try again below.
        </div>
      ) }

      { generating && (
        <div className='flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-6'>
          <span className='h-5 w-5 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent' />
          <div>
            <div className='text-sm font-semibold text-slate-800'>Writing the blueprint…</div>
            <div className='text-xs text-slate-500'>This usually takes under a minute. You can leave this page.</div>
          </div>
        </div>
      ) }

      { showJdForm && !generating && !locked && (
        <div className='flex flex-col gap-3 rounded-xl border border-slate-200 p-4'>
          <label className='flex flex-col gap-1'>
            <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Job description</span>
            <textarea
                rows={10}
                className={textareaClass}
                value={jobDescription}
                placeholder='Paste the job description'
                onChange={(e) => { setJobDescription(e.target.value); setDirty(true); }}
            />
          </label>
          <label className='flex flex-col gap-1'>
            <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Notes for the generator (optional)</span>
            <textarea
                rows={3}
                className={textareaClass}
                value={notes}
                placeholder='For example: focus on enterprise customers; must handle on-call escalations'
                onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
            />
          </label>
          <div className='flex items-center gap-2'>
            <button
                type='button'
                className={primaryButton}
                disabled={isGenerating || jobDescription.trim().length < 20}
                onClick={handleGenerate}
            >
              { draft ? 'Regenerate blueprint' : 'Generate blueprint' }
            </button>
            { showRegenerate && (
              <button type='button' className={secondaryButton} onClick={() => setShowRegenerate(false)}>Cancel</button>
            ) }
            { draft && <span className='text-xs text-slate-500'>Regenerating replaces the current draft.</span> }
          </div>
        </div>
      ) }

      { draft && !generating && (
        <>
          <BlueprintEditor
              draft={draft}
              config={config}
              readOnly={locked}
              onChange={(d) => { setDraft(d); setDirty(true); }}
              onConfigChange={(c) => { setConfig(c); setDirty(true); }}
          />
          { !!assessment.approvalProblems?.length && !dirty && (
            <ul className='list-disc pl-5 text-xs text-amber-700'>
              { assessment.approvalProblems.map((p) => <li key={p}>{ p }</li>) }
            </ul>
          ) }
          { !locked && (
            <div className='flex flex-wrap items-center gap-2'>
              <button type='button' className={secondaryButton} disabled={!dirty || isSaving} onClick={handleSave}>
                Save draft
              </button>
              <button
                  type='button'
                  className={primaryButton}
                  disabled={isApproving || isSaving || (status === 'Approved' && !dirty)}
                  onClick={handleApprove}
              >
                { status === 'Approved' && !dirty ? 'Approved' : 'Approve blueprint' }
              </button>
              { !showRegenerate && (
                <button type='button' className='text-sm font-semibold text-slate-500 hover:text-slate-800' onClick={() => setShowRegenerate(true)}>
                  Regenerate from the job description
                </button>
              ) }
              { status === 'Approved' && !dirty && (
                <button type='button' className='ml-auto text-sm font-semibold text-emerald-700 hover:text-emerald-800' onClick={onGoToCandidates}>
                  Invite candidates →
                </button>
              ) }
            </div>
          ) }
        </>
      ) }
    </div>
  );
};
